// composables/layout/useBreadcrumb.ts
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useGnbStore } from '~/store/layout/gnb';
import type { BreadcrumbType, DepthITemType } from '~/types/layout/gnb';
import type { SelectValue } from 'ant-design-vue/es/select';

const normalize = (p?: string): string => {
    const noQuery = (p ?? '').split('?')[0] ?? '';
    const noHash  = noQuery.split('#')[0] ?? '';
    return ('/' + noHash).replace(/\/{2,}/g, '/').replace(/\/$/, '').toLowerCase();
};
const matchesChild = (child: DepthITemType, path: string) => {
    const u = normalize(child.url)
    if (path.startsWith(u)) return true

    // '/.../list' → '/...' 프리픽스도 허용
    if (u.endsWith('/list')) {
        const base = u.slice(0, -5)
        if (path === base || path.startsWith(base + '/')) return true
    }

    // ✅ aliases 지원: '/data/uribrief', '/data/uccibrief' 같은 상세 프리픽스 허용
    for (const a of child.aliases ?? []) {
        const an = normalize(a)
        if (path === an || path.startsWith(an + '/')) return true
    }
    return false
}
export function useBreadcrumb() {
    const route = useRoute();
    const router = useRouter();
    const { firstdepth } = storeToRefs(useGnbStore());

    const isVisible = (n?: DepthITemType) => (n?.showgnb !== false);

    const path = computed(() => normalize(route.path));
    const segments = computed(() =>
        path.value.split('/').filter(Boolean).map((_, i, arr) => '/' + arr.slice(0, i + 1).join('/'))
    );


    type BreadcrumbOverride = { label: string; url?: string };
    const overrideItem = computed<BreadcrumbOverride | null>(() => {
        const meta: any = route.meta ?? {};

        if (meta?.breadcrumb && meta.breadcrumb.label) {
            const b = meta.breadcrumb as any;
            return { label: String(b.label), url: b.url ?? path.value };
        }
        return null;
    });
    const showSelects = computed(() => !overrideItem.value); // 오버라이드면 셀렉트 숨김

    /** 1차 찾기 */
    const currentFirst = computed<DepthITemType | undefined>(() => {
        return firstdepth.value
            .filter(isVisible)
            .find(fd => {
                const fdUrl = normalize(fd.url);
                if (segments.value.includes(fdUrl)) return true;
                const fdBase    = '/' + fdUrl.split('/').filter(Boolean)[0];
                const routeBase = '/' + path.value.split('/').filter(Boolean)[0];
                return fdBase === routeBase;
            });
    });

    const currentChildren = computed<DepthITemType[]>(
        () => ((currentFirst.value?.children ?? []).filter(isVisible)) as DepthITemType[]
    );

    /** 2차 찾기 */
    const currentSecond = computed<DepthITemType | undefined>(() => {
        // 1) 정확히 같은 URL 우선
        const exact = currentChildren.value.find(cd => normalize(cd.url) === path.value)
        if (exact) return exact

        // 2) 상세 경로 매칭: '/.../list' 는 '/...' prefix 로도 인정
        const candidates = currentChildren.value
          .filter(cd => matchesChild(cd, path.value))
          .sort((a, b) => normalize(b.url).length - normalize(a.url).length)

        return candidates[0]
    })

    /** 브레드크럼 텍스트 */
    const breadcrumbItems = computed<BreadcrumbType[]>(() => {

        if (overrideItem.value) {
            return [{ label: overrideItem.value.label, url: overrideItem.value.url ?? path.value }];
        }
        const items: BreadcrumbType[] = [];
        if (currentFirst.value && isVisible(currentFirst.value))
            items.push({ label: currentFirst.value.label, url: currentFirst.value.url });
        if (currentSecond.value && isVisible(currentSecond.value))
            items.push({ label: currentSecond.value.label, url: currentSecond.value.url });
        return items;
    });

    /** Select */
    const firstOptions = computed(
        () =>
            firstdepth.value
                .filter(isVisible)
                .map(fd => ({
                    label: fd.label,
                    value: normalize(fd.url),
                    raw: fd,
                })) as Array<{ label: string; value: string; raw: DepthITemType }>
    );

    const secondOptions = computed(() =>
        currentChildren.value.map(cd => ({
            label: cd.label,
            value: normalize(cd.url),
            raw: cd,
        }))
    );

    /** 현재 선택값 */
    const firstValue = computed(() => normalize(currentFirst.value?.url ?? ''));
    const secondValue = computed(() => normalize(currentSecond.value?.url ?? ''));

    const extract = (value: SelectValue): string => {
        if (Array.isArray(value)) value = value[0] as any;
        if (typeof value === 'object' && value && 'value' in value) return String((value as any).value);
        return String(value ?? '');
    };

    const onChangeFirst = (value: SelectValue) => {
        const val = extract(value);
        const found = firstOptions.value.find(o => normalize(o.raw.url) === val)?.raw;
        const target =
            found?.url && found.url !== '#'
                ? found.url
                : found?.children?.find(isVisible)?.url ?? '/';
        router.push(target);
    };

    const onChangeSecond = (value: SelectValue) => {
        const val = extract(value);
        const found = secondOptions.value.find(o => normalize(o.raw.url) === val)?.raw;
        if (found?.url) router.push(found.url);
    };

    return {
        breadcrumbItems,
        firstOptions,
        secondOptions,
        firstValue,
        secondValue,
        onChangeFirst,
        onChangeSecond,
        showSelects,
    };
}
